import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Col,
    Form,
    InputNumber,
    Modal,
    notification,
    Row,
    Segmented,
    Select,
    Tooltip,
    Typography
} from 'antd';
import {DollarOutlined, InfoCircleOutlined, WarningOutlined} from '@ant-design/icons';
import {ApiKey, tradingService} from '../../../services/tradingService';
import {accountDetailsService} from '../../../services/accountDetailsService';
import {estimateContractQuantity} from '../../../utils/contractQuantity';
import {useThrottle} from '../../../hooks/useThrottle';
import {formatEffectiveDecimal, truncateToDecimalPlaces} from '../../../utils/numberFormatter';
import {PositionModel} from '../../../types/okxPosition';
import AmountSelector from './AmountSelector';
import Top30InstrumentSelector from './Top30InstrumentSelector';
import InstrumentDetailPanel from './InstrumentDetailPanel';
import './TradingForm.css';

const {Text} = Typography;
const MIN_ORDER_MARGIN_USDT = 5;
/** 现货常用比例快捷键 */
const SPOT_QUICK_PERCENTS = [25, 50, 75, 100];
/** 现货默认买入比例 */
const SPOT_DEFAULT_PERCENT = 25;

/** 交易成功事件详情，用于前端精确匹配持仓/委托 */
export interface TradeEvent {
    apiKeyId: number;
    instId: string;
    side: string;       // 'buy' | 'sell'
    posSide: string;    // 'long' | 'short'
    orderType: string;  // 'market' | 'limit'
    px?: string;        // 限价单价格
}

interface TradingFormProps {
    apiKeys: ApiKey[];
    selectedApiKey: number | null;
    tradeMode?: 'SPOT' | 'SWAP';  // 交易模式: SWAP=永续(默认), SPOT=现货
    onTradeSuccess: (event: TradeEvent) => void;
}

const TradingForm: React.FC<TradingFormProps> = ({
                                                     apiKeys,
                                                     selectedApiKey,
                                                     tradeMode = 'SWAP',
                                                     onTradeSuccess
                                                 }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedInstrument, setSelectedInstrument] = useState<string>('');
    // 现货交易:买入/卖出方向(决定百分比基数:USDT可用余额 or 现货持仓)
    const [spotSide, setSpotSide] = useState<'buy' | 'sell'>('buy');
    // 现货比例(买入=可用USDT比例;卖出=可卖持仓比例;持仓低于最小交易单位时强制100%)
    const [spotPercent, setSpotPercent] = useState<number>(SPOT_DEFAULT_PERCENT);
    // 现货持仓(余额派生,后端 /positions/spot 已剔除USDT并过滤余额<=0及不足1 lot)
    const [spotPositions, setSpotPositions] = useState<PositionModel[]>([]);
    const [spotPositionsLoading, setSpotPositionsLoading] = useState(false);
    // 最终换算后的现货下单数量(币),由 handleSpotSubmit 计算,确认弹窗/下单均读取该ref保证一致
    const spotQtyRef = useRef<number | null>(null);
    const [selectedAllocationPercent, setSelectedAllocationPercent] = useState<number>(5);
    const [orderType, setOrderType] = useState<'market' | 'limit'>('market');
    const [limitPrice, setLimitPrice] = useState<number | null>(null);
    const [takeProfitEnabled, setTakeProfitEnabled] = useState(false);
    const [stopLossEnabled, setStopLossEnabled] = useState(false);
    const [takeProfitPrice, setTakeProfitPrice] = useState<number | null>(null);
    const [stopLossPrice, setStopLossPrice] = useState<number | null>(null);
    const [takeProfitPct, setTakeProfitPct] = useState<number | null>(null);
    const [stopLossPct, setStopLossPct] = useState<number | null>(null);
    const [useTakeProfitPct, setUseTakeProfitPct] = useState(true);  // 默认使用百分比模式
    const [useStopLossPct, setUseStopLossPct] = useState(true);      // 默认使用百分比模式
    const [currentPrice, setCurrentPrice] = useState<number | null>(null);  // 当前合约的实际价格
    const [fourHourAvgChange, setFourHourAvgChange] = useState<number | null>(null);  // 4小时平均涨跌幅
    const [estimatedQuantity, setEstimatedQuantity] = useState<number>(0);  // 预估数量
    const [estimatedNotional, setEstimatedNotional] = useState<number>(0);  // 预估名义价值
    const [currentMarkPrice, setCurrentMarkPrice] = useState<number | null>(null);  // 当前标记价格
    const [contractSpec, setContractSpec] = useState<{ ctVal?: number; lotSz?: number; minSz?: number }>({});

    // 价格编辑状态管理
    const [hasManuallyEditedPrice, setHasManuallyEditedPrice] = useState(false);  // 用户是否手动编辑过价格
    const [isUserEditing, setIsUserEditing] = useState(false);  // 用户是否正在编辑
    const [priceFlashClass, setPriceFlashClass] = useState('');  // 价格更新时的闪烁动画class

    // 止盈止损加载状态
    const [isCalculatingProfitLoss, setIsCalculatingProfitLoss] = useState(false);

    // 默认止盈止损百分比（基于3倍杠杆）
    const DEFAULT_TAKE_PROFIT_PCT = parseFloat((0.8 / 3 * 100).toFixed(2)); // 26.67%
    const DEFAULT_STOP_LOSS_PCT = parseFloat((0.6 / 3 * 100).toFixed(2));   // 20%

    // 交易模式(永续/现货)切换时重置已选品种
    // 避免跨模式滞留旧品种(如 SWAP 的 BTC-USDT-SWAP 在现货模式下仍显示其波动数据),由用户在品种选择器重新选择
    useEffect(() => {
        setSelectedInstrument('');
        // 切回现货模式时重置方向与比例,避免卖出上下文残留
        setSpotSide('buy');
        setSpotPercent(SPOT_DEFAULT_PERCENT);
    }, [tradeMode]);

    // 处理合约变化时清除价格和预估信息，并重置止盈止损状态
    useEffect(() => {
        if (selectedInstrument) {
            setCurrentPrice(null); // 切换合约时清除之前的价格
            setFourHourAvgChange(null); // 清除4小时平均涨跌幅数据
            setEstimatedQuantity(0); // 清除预估数量
            setEstimatedNotional(0); // 清除预估名义价值
            setContractSpec({});

            // 止盈止损统一对齐:现货买入与永续一致,默认勾选+百分比模式+默认百分比(4H波动率到位后由智能填充覆盖)
            setTakeProfitEnabled(true);
            setStopLossEnabled(true);
            // 使用默认公式设置百分比
            setTakeProfitPct(DEFAULT_TAKE_PROFIT_PCT);
            setStopLossPct(DEFAULT_STOP_LOSS_PCT);
            // 重置为百分比模式
            setUseTakeProfitPct(true);
            setUseStopLossPct(true);
            // 清除之前的价格设置
            setTakeProfitPrice(null);
            setStopLossPrice(null);

            // 重置价格编辑状态标识
            setHasManuallyEditedPrice(false);
            setIsUserEditing(false);

            // console.log(`[TradingForm] 合约切换: ${selectedInstrument}, 默认止盈=${DEFAULT_TAKE_PROFIT_PCT}%, 止损=${DEFAULT_STOP_LOSS_PCT}%`);
        } else {
            // 合约清空时，止盈止损也设为未选中状态
            setTakeProfitEnabled(false);
            setStopLossEnabled(false);
            // 清除所有设置
            setTakeProfitPrice(null);
            setStopLossPrice(null);
            setTakeProfitPct(null);
            setStopLossPct(null);
            // 清除限价
            setLimitPrice(null);
        }
    }, [selectedInstrument, orderType]);

    useEffect(() => {
        const loadContractSpec = async () => {
            if (!selectedInstrument || !selectedApiKey) {
                setContractSpec({});
                return;
            }
            try {
                // 规格优先走 /spec(本地 t_cex_instruments,轻量且权威),现货 lotSz/minSz 同源
                const spec = await tradingService.getInstrumentSpec(selectedInstrument, selectedApiKey);
                setContractSpec({
                    ctVal: spec.ctVal,
                    lotSz: spec.lotSz,
                    minSz: spec.minSz
                });
            } catch (error) {
                try {
                    // 规格表未命中时回退 overview(含 5m/4H/1D 波动率字段,规格字段一致)
                    const overview = await tradingService.getInstrumentOverview(selectedInstrument, selectedApiKey, ['5m', '4H', '1D']);
                    setContractSpec({
                        ctVal: overview.ctVal,
                        lotSz: overview.lotSz,
                        minSz: overview.minSz
                    });
                } catch (e2) {
                    setContractSpec({});
                }
            }
        };

        loadContractSpec();
    }, [selectedInstrument, selectedApiKey]);

    // 自动计算止盈止损百分比（止盈1.2倍，止损1.5倍）
    useEffect(() => {
        // 当合约被选中时,开始计算
        if (selectedInstrument) {
            setIsCalculatingProfitLoss(true);
        }

        if (fourHourAvgChange !== null && fourHourAvgChange !== 0) {
            const takeProfitPctCalculated = parseFloat((Math.abs(fourHourAvgChange) * 1.2).toFixed(2));
            const stopLossPctCalculated = parseFloat((Math.abs(fourHourAvgChange) * 1.5).toFixed(2));

            // 智能值覆盖默认值
            setTakeProfitPct(takeProfitPctCalculated);
            setStopLossPct(stopLossPctCalculated);
            // console.log(`[TradingForm] 智能更新止盈止损: TP=${takeProfitPctCalculated}%, SL=${stopLossPctCalculated}% (基于4H平均: ${Math.abs(fourHourAvgChange).toFixed(2)}%)`);

            setIsCalculatingProfitLoss(false);
        }
    }, [fourHourAvgChange, selectedInstrument, takeProfitPct, stopLossPct]);

    // 账户保证金与合约规格无关；选择账户后立即拉取，比例变化时前端直接重算。
    const [accountAvailableMargin, setAccountAvailableMargin] = useState<number | null>(null);
    const [accountBalanceLoading, setAccountBalanceLoading] = useState(false);
    const [previewActualMargin, setPreviewActualMargin] = useState<number | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        if (!selectedApiKey) {
            setAccountAvailableMargin(null);
            setAccountBalanceLoading(false);
            return;
        }

        let cancelled = false;
        setAccountBalanceLoading(true);
        accountDetailsService.getAccountDetails(selectedApiKey).then((response) => {
            if (cancelled) return;
            const availableBalance = Number(response.data?.data?.availableBalance);
            setAccountAvailableMargin(Number.isFinite(availableBalance) && availableBalance >= 0 ? availableBalance : null);
        }).catch(() => {
            if (!cancelled) setAccountAvailableMargin(null);
        }).finally(() => {
            if (!cancelled) setAccountBalanceLoading(false);
        });

        return () => {
            cancelled = true;
        };
    }, [selectedApiKey]);

    const selectedMargin = useMemo(() => {
        if (accountAvailableMargin === null || selectedAllocationPercent <= 0) return null;
        return accountAvailableMargin * (selectedAllocationPercent / 100);
    }, [accountAvailableMargin, selectedAllocationPercent]);

    // ===== 现货:持仓/余额 与 数量换算(买入按USDT比例,卖出按持仓比例) =====
    // 现货持仓(余额派生,/positions/spot 已剔除 USDT 并过滤余额<=0/不足1 lot)
    const loadSpotPositions = useCallback(async (apiKeyId: number) => {
        try {
            const resp = await tradingService.getSpotPositions(apiKeyId);
            setSpotPositions(resp.success ? (resp.data ?? []) : []);
        } catch (e) {
            // 静默:失败时沿用旧数据,下单校验会兜底提示
        }
    }, []);

    useEffect(() => {
        if ('SPOT' !== tradeMode || !selectedApiKey) {
            setSpotPositions([]);
            return;
        }
        let cancelled = false;
        // 先清空旧Key持仓,避免切换API Key时短暂展示上一账户资产
        setSpotPositions([]);
        setSpotPositionsLoading(true);
        loadSpotPositions(selectedApiKey)
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setSpotPositionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [selectedApiKey, tradeMode, loadSpotPositions]);

    // 现货最小下单量/步长(合约规格来源:getInstrumentSpec)
    const spotMinQty = contractSpec.lotSz && contractSpec.lotSz > 0
        ? contractSpec.lotSz
        : (contractSpec.minSz && contractSpec.minSz > 0 ? contractSpec.minSz : null);
    const spotLotSz = contractSpec.lotSz && contractSpec.lotSz > 0 ? contractSpec.lotSz : null;

    // 选中交易对在现货持仓中的记录(可卖=availPos,总量=pos/baseBal,与资产卡口径一致)
    const spotHolding = useMemo(() => {
        if (!selectedInstrument) return null;
        const hit = spotPositions.find(p => p.instId === selectedInstrument);
        if (!hit) return null;
        const total = Number(hit.pos ?? hit.baseBal ?? 0) || 0;
        const available = Number(hit.availPos ?? hit.pos ?? hit.baseBal ?? 0) || 0;
        return {total, available};
    }, [spotPositions, selectedInstrument]);

    // 持仓低于最小交易单位:卖出锁定100%全额卖出,比例不可调整
    const spotSellForcedFull = !!spotHolding && !!spotMinQty
        && spotHolding.available > 0 && spotHolding.available < spotMinQty;
    // 卖出生效比例(持仓不足一最小单位时固定100%)
    const spotEffectiveSellPercent = spotSellForcedFull ? 100 : spotPercent;

    // 按步长(lotSz)向下规整现货数量,避免交易所整数倍校验失败
    // 注意加极小epsilon:浮点除法(如 0.00075/0.0001)可能得 7.499999…,直接floor会少规整一个lot,
    // 持仓通常为lot整数倍,epsilon(1e-6 lot)足以纠正尾差且不会误提升非整数倍数量
    const floorSpotQtyByLot = useCallback((rawQty: number): number => {
        if (rawQty <= 0) return 0;
        if (!spotLotSz || !(spotLotSz > 0)) {
            return truncateToDecimalPlaces(rawQty, 8);
        }
        const lots = Math.floor(rawQty / spotLotSz + 1e-6);
        const floored = lots * spotLotSz;
        return floored > 0 ? truncateToDecimalPlaces(floored, 8) : 0;
    }, [spotLotSz]);

    // 下单/换算基准价格(限价单用委托价,市价单用实时价)
    const spotPriceForCalc = useMemo(() => {
        return (orderType === 'limit' && limitPrice && limitPrice > 0) ? limitPrice : currentPrice;
    }, [orderType, limitPrice, currentPrice]);

    // 现货预估数量:买入=可用USDT×比例÷价格;卖出=可卖持仓×比例(均按lotSz向下规整)
    const spotEstQty = useMemo(() => {
        if ('buy' === spotSide) {
            if (accountAvailableMargin === null || !spotPriceForCalc || spotPriceForCalc <= 0) return null;
            const budget = accountAvailableMargin * (spotPercent / 100);
            if (budget <= 0) return null;
            return floorSpotQtyByLot(budget / spotPriceForCalc);
        }
        if (!spotHolding || spotHolding.available <= 0) return null;
        return floorSpotQtyByLot(spotHolding.available * (spotEffectiveSellPercent / 100));
    }, [spotSide, accountAvailableMargin, spotPriceForCalc, spotPercent, spotHolding, floorSpotQtyByLot, spotEffectiveSellPercent]);

    // 现货预估金额(数量×价格)
    const spotEstUsdt = useMemo(() => {
        if (spotEstQty === null || !spotPriceForCalc || spotPriceForCalc <= 0) return null;
        return spotEstQty * spotPriceForCalc;
    }, [spotEstQty, spotPriceForCalc]);

    // 合约预览只用于按交易所规格规整数量和最终保证金，不承担账户余额展示。
    useEffect(() => {
        if (!selectedApiKey || !selectedInstrument || !selectedAllocationPercent || selectedAllocationPercent <= 0) {
            setPreviewActualMargin(null);
            setPreviewLoading(false);
            return;
        }
        let cancelled = false;
        setPreviewLoading(true);
        tradingService.previewOrderSizing({
            apiKeyId: selectedApiKey,
            instId: selectedInstrument,
            allocationPercent: selectedAllocationPercent,
            side: 'buy',
            orderType,
            lever: 3,
            px: orderType === 'limit' && limitPrice ? limitPrice : undefined,
            source: 'web'
        }).then((resp: any) => {
            if (!cancelled) {
                if (resp?.data?.success && resp.data.data) {
                    setPreviewActualMargin(resp.data.data.actualMargin ?? null);
                } else {
                    setPreviewActualMargin(null);
                }
                setPreviewLoading(false);
            }
        }).catch(() => {
            if (!cancelled) {
                setPreviewActualMargin(null);
                setPreviewLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [selectedApiKey, selectedInstrument, selectedAllocationPercent, orderType, limitPrice]);

    // API 是否已完成加载（不论结果是否可用）
    const previewLoaded = !previewLoading;
    // 是否有有效的实际保证金估算
    const previewHasMargin = previewActualMargin !== null;

    // 计算预估信息（仅在有预览结果时估算，否则显示不可用）
    useEffect(() => {
        if (!currentPrice || !selectedAllocationPercent || selectedAllocationPercent <= 0) {
            setEstimatedQuantity(0);
            setEstimatedNotional(0);
            return;
        }
        if (!previewLoaded) {
            setEstimatedQuantity(0);
            setEstimatedNotional(0);
            return;
        }

        const lever = 3;
        const estimatedMargin = previewActualMargin || 0;
        const priceForCalculation = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
        const quantity = estimateContractQuantity({
            amount: estimatedMargin,
            leverage: lever,
            price: priceForCalculation,
            ctVal: contractSpec.ctVal,
            lotSz: contractSpec.lotSz,
            minSz: contractSpec.minSz
        });

        const notional = estimatedMargin * lever;

        setEstimatedQuantity(quantity || 0);
        setEstimatedNotional(notional || 0);
    }, [currentPrice, selectedAllocationPercent, orderType, limitPrice, contractSpec, previewActualMargin]);

    // 错误处理函数
    const handleTradingError = (error: any) => {
        // console.error('下单失败:', error);

        const errorMessage = error.response?.data?.message || error.message || '下单失败，请重试';

        // 根据错误类型进行分类处理
        if (errorMessage.includes('API Key') || errorMessage.includes('50111') || errorMessage.includes('50112') || errorMessage.includes('50113')) {
            // API Key相关错误
            notification.error({
                message: 'API Key配置错误',
                description: (
                    <div>
                        <p>{errorMessage}</p>
                        <p style={{marginTop: 8, fontSize: '12px', color: '#666'}}>
                            请检查以下配置是否正确：
                        </p>
                        <ul style={{marginTop: 4, fontSize: '12px', color: '#666'}}>
                            <li>Access Key（API Key）</li>
                            <li>Secret Key（密钥）</li>
                            <li>Pass Phrase（密码）</li>
                            <li>API Key权限和IP白名单设置</li>
                        </ul>
                    </div>
                ),
                duration: 8,
                placement: 'topRight'
            });
        } else if (errorMessage.includes('Invalid signature') || errorMessage.includes('签名')) {
            // 签名错误
            notification.error({
                message: '签名验证失败',
                description: (
                    <div>
                        <p>{errorMessage}</p>
                        <p style={{marginTop: 8, fontSize: '12px', color: '#666'}}>
                            可能的原因：Secret Key不正确或时间同步问题
                        </p>
                    </div>
                ),
                duration: 6,
                placement: 'topRight'
            });
        } else if (errorMessage.includes('Insufficient') || errorMessage.includes('余额') || errorMessage.includes('保证金')) {
            // 余额不足错误
            notification.error({
                message: '账户余额不足',
                description: errorMessage,
                duration: 5,
                placement: 'topRight'
            });
        } else if (errorMessage.includes('参数') || errorMessage.includes('parameter')) {
            // 参数错误
            Modal.error({
                title: <span style={{color: '#ffffff'}}>参数错误</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        {errorMessage}
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
        } else {
            // 通用错误
            Modal.error({
                title: <span style={{color: '#ffffff'}}>错误</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        {errorMessage}
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
        }
    };

    const showMinimumMarginWarning = (margin: number) => {
        Modal.warning({
            title: <span style={{color: '#ffffff'}}>保证金不足</span>,
            content: (
                <div style={{color: '#a0a0a0'}}>
                    <p>当前比例折算保证金仅 <span style={{
                        color: 'var(--pt-bear)',
                        fontWeight: 'bold'
                    }}>{margin.toFixed(2)} USDT</span>，低于最低要求 <strong>{MIN_ORDER_MARGIN_USDT} USDT</strong>。</p>
                    <p style={{marginTop: 8}}>请提高开仓比例或增加账户可用余额。</p>
                </div>
            ),
            centered: true,
            className: 'dark-theme-modal'
        });
    };

    // 显示交易确认对话框
    const showTradingConfirmDialog = async (direction: 'long' | 'short', side: 'buy' | 'sell', apiKeyData: ApiKey) => {
        const orderPrice = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;

        const lever = 3;
        // 无预览时确认弹窗显示比例而非金额
        const quantity = (previewHasMargin && orderPrice) ? estimateContractQuantity({
            amount: previewActualMargin!,
            leverage: lever,
            price: orderPrice,
            ctVal: contractSpec.ctVal,
            lotSz: contractSpec.lotSz,
            minSz: contractSpec.minSz
        }) : 0;

        // 计算止盈止损价格
        let takeProfitPriceCalculated: number | null = null;
        let stopLossPriceCalculated: number | null = null;

        if (takeProfitEnabled && takeProfitPct && orderPrice) {
            if (direction === 'long') {
                takeProfitPriceCalculated = orderPrice * (1 + takeProfitPct / 100);
            } else {
                takeProfitPriceCalculated = orderPrice * (1 - takeProfitPct / 100);
            }
        }

        if (stopLossEnabled && stopLossPct && orderPrice) {
            if (direction === 'long') {
                stopLossPriceCalculated = orderPrice * (1 - stopLossPct / 100);
            } else {
                stopLossPriceCalculated = orderPrice * (1 + stopLossPct / 100);
            }
        }

        const isLiveTrading = apiKeyData.isLiveTrading;
        const title = isLiveTrading ? '交易确认' : '交易确认';
        const riskColor = isLiveTrading ? 'var(--pt-bear)' : 'var(--pt-primary)';

        Modal.confirm({
            title: (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    {isLiveTrading && <WarningOutlined style={{color: riskColor}}/>}
                    <span style={{color: '#ffffff'}}>{title}</span>
                </div>
            ),
            icon: null,
            width: 480,
            centered: true,
            className: 'dark-theme-modal',
            content: (
                <div style={{fontSize: '14px', lineHeight: '1.6'}}>
                    {isLiveTrading && (
                        <p style={{color: riskColor, fontWeight: 'bold', marginBottom: '16px'}}>
                            ⚠️ 您正在进行实盘交易，资金将真实投入市场
                        </p>
                    )}

                    <div style={{
                        background: 'rgba(7, 10, 18, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #404040'
                    }}>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}}
                                       strong>{'SPOT' === tradeMode ? '交易对：' : '合约品种：'}</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>{selectedInstrument}</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>交易方向：</Text></Col>
                            <Col><Text style={{color: direction === 'long' ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                                {direction === 'long' ? '开多' : '开空'}
                            </Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>订单类型：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>{orderType === 'market' ? '市价单' : '限价单'}</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>委托价格：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>
                                {orderType === 'limit' && limitPrice
                                    ? `$${limitPrice.toFixed(4)}`
                                    : orderPrice
                                        ? `$${orderPrice.toFixed(4)} (市价)`
                                        : '获取中...'
                                }
                            </Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>开仓比例：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>{selectedAllocationPercent}%</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>预估保证金：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>待服务端计算</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>杠杆倍数：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>{lever}x</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>预估数量：</Text></Col>
                            <Col><Text
                                style={{color: '#ffffff'}}>{formatEffectiveDecimal(quantity, 2, 0, 8)}</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>预估名义价值：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>${notional}</Text></Col>
                        </Row>

                        {(takeProfitEnabled && (takeProfitPct || takeProfitPrice)) && (
                            <Row justify="space-between" style={{marginBottom: '8px'}}>
                                <Col><Text style={{color: '#a0a0a0'}} strong>止盈价格：</Text></Col>
                                <Col><Text style={{color: 'var(--pt-bull)'}}>
                                    {takeProfitPrice
                                        ? `$${takeProfitPrice.toFixed(4)}`
                                        : takeProfitPriceCalculated
                                            ? `$${takeProfitPriceCalculated.toFixed(4)}`
                                            : '计算中...'
                                    }
                                    {(() => {
                                        if (takeProfitPrice && orderPrice) {
                                            const percentage = ((takeProfitPrice - orderPrice) / orderPrice) * 100;
                                            return ` (${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%)`;
                                        }
                                        return takeProfitPct && !takeProfitPrice ? ` (${takeProfitPct}%)` : '';
                                    })()}
                                </Text></Col>
                            </Row>
                        )}

                        {(stopLossEnabled && (stopLossPct || stopLossPrice)) && (
                            <Row justify="space-between">
                                <Col><Text style={{color: '#a0a0a0'}} strong>止损价格：</Text></Col>
                                <Col><Text style={{color: 'var(--pt-bear)'}}>
                                    {stopLossPrice
                                        ? `$${stopLossPrice.toFixed(4)}`
                                        : stopLossPriceCalculated
                                            ? `$${stopLossPriceCalculated.toFixed(4)}`
                                            : '计算中...'
                                    }
                                    {(() => {
                                        if (stopLossPrice && orderPrice) {
                                            const percentage = ((stopLossPrice - orderPrice) / orderPrice) * 100;
                                            return ` (${percentage > 0 ? '+' : ''}${percentage.toFixed(2)}%)`;
                                        }
                                        return stopLossPct && !stopLossPrice ? ` (${stopLossPct}%)` : '';
                                    })()}
                                </Text></Col>
                            </Row>
                        )}
                    </div>

                    <p style={{
                        color: isLiveTrading ? riskColor : '#a0a0a0',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        margin: 0
                    }}>
                        确认执行{'SPOT' === tradeMode ? '现货' : '合约'}交易吗？
                    </p>
                </div>
            ),
            okText: '确认交易',
            cancelText: '取消',
            okButtonProps: {
                danger: false, // 禁用danger样式，完全使用自定义样式
                type: 'primary',
                size: 'middle',
                style: {
                    backgroundColor: direction === 'long' ? 'var(--pt-bull)' : 'var(--pt-bear)',
                    borderColor: direction === 'long' ? 'var(--pt-bull)' : 'var(--pt-bear)',
                    boxShadow: 'none',
                    color: '#ffffff'
                },
                className: direction === 'long' ? 'buy-confirm-button' : 'sell-confirm-button'
            },
            cancelButtonProps: {
                size: 'middle'
            },
            onOk: () => executeOrder(side, direction),
        });
    };

    // 现货下单:数量与最小下单量校验后弹出确认
    const handleSpotSubmit = async (side: 'buy' | 'sell') => {
        if (!selectedApiKey) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>请选择API Key</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        请先选择一个API Key再进行交易
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        if (!selectedInstrument) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>请选择交易对</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        请先选择一个交易对再进行交易
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        const selectedApiKeyData = apiKeys.find(key => key.keyId === selectedApiKey);
        if (!selectedApiKeyData) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>API Key不存在</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        所选择的API Key不存在，请重新选择
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        // 方向同步到UI(买入/卖出上下文:金额区基数=可用余额/现货持仓)
        setSpotSide(side);

        // 成交基准价格(市价单用实时价,限价单用委托价)
        const price = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
        if (!price || price <= 0) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>价格获取中</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        正在获取{selectedInstrument}的最新价格，请稍后重试
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        let qty = 0;
        if ('buy' === side) {
            // 买入:可用余额 × 比例 → USDT金额 → 币数量(按lotSz向下规整)
            if (accountAvailableMargin === null) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>余额获取中</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            正在获取账户可用余额，请稍后重试
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                return;
            }
            const budget = accountAvailableMargin * (spotPercent / 100);
            if (budget < MIN_ORDER_MARGIN_USDT) {
                Modal.warning({
                    title: <span style={{color: '#ffffff'}}>买入金额过低</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            <p>当前比例折算买入金额仅 <strong
                                style={{color: 'var(--pt-amber)'}}>{budget.toFixed(2)} USDT</strong>，低于最低下单金额 <strong>{MIN_ORDER_MARGIN_USDT} USDT</strong>。
                            </p>
                            <p style={{marginTop: 8}}>请提高买入比例或增加账户可用余额。</p>
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                return;
            }
            qty = floorSpotQtyByLot(budget / price);
        } else {
            // 卖出:可卖持仓 × 比例 → 币数量(持仓低于最小交易单位时全额卖出)
            const available = spotHolding ? spotHolding.available : 0;
            if (available <= 0) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>暂无可卖持仓</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            当前交易对 {selectedInstrument} 没有可卖出的现货持仓
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                return;
            }
            const effectivePct = spotSellForcedFull ? 100 : spotPercent;
            qty = floorSpotQtyByLot(available * (effectivePct / 100));
        }

        // 最小下单量校验(lotSz/minSz)
        // 例外:持仓低于最小交易单位的全额卖出(强制100%)按可卖数量原样提交,交由交易所按实际可卖撮合
        const isForcedFullSell = 'sell' === side && spotSellForcedFull;
        if (!isForcedFullSell && (qty <= 0 || (spotMinQty && qty < spotMinQty))) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>低于最小下单量</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        {side === 'sell'
                            ? '所选比例折算的卖出数量低于最小下单量，请提高卖出比例或选择100%全额卖出'
                            : `最小下单量为 ${formatEffectiveDecimal(spotMinQty!, 2, 0, 8)}，请提高买入比例`}
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        // 记录最终换算数量,确认弹窗与真正下单均读取该值,保证两处一致
        spotQtyRef.current = qty;
        showSpotConfirmDialog(side, selectedApiKeyData);
    };

    // 现货交易确认对话框
    const showSpotConfirmDialog = (side: 'buy' | 'sell', apiKeyData: ApiKey) => {
        const orderPrice = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
        const isLiveTrading = apiKeyData.isLiveTrading;
        // 下单数量:handleSpotSubmit 已按方向(可用余额/可卖持仓)换算并按lotSz规整
        const orderQty = spotQtyRef.current ?? 0;
        const orderUsd = orderQty > 0 && orderPrice && orderPrice > 0 ? orderQty * orderPrice : 0;
        // 现货止盈止损仅买入方向有效(卖出后已无持仓,后端拒绝挂策略委托):持有即看涨,按方向long计算触发价
        let tpPriceDisplay: number | null = null;
        let slPriceDisplay: number | null = null;
        if ('buy' === side) {
            if (takeProfitEnabled) {
                tpPriceDisplay = (useTakeProfitPct && takeProfitPct && takeProfitPct > 0)
                    ? (orderPrice ? orderPrice * (1 + takeProfitPct / 100) : null)
                    : (takeProfitPrice && takeProfitPrice > 0 ? takeProfitPrice : null);
            }
            if (stopLossEnabled) {
                slPriceDisplay = (useStopLossPct && stopLossPct && stopLossPct > 0)
                    ? (orderPrice ? orderPrice * (1 - stopLossPct / 100) : null)
                    : (stopLossPrice && stopLossPrice > 0 ? stopLossPrice : null);
            }
        }

        Modal.confirm({
            title: (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    {isLiveTrading && <WarningOutlined style={{color: 'var(--pt-bear)'}}/>}
                    <span style={{color: '#ffffff'}}>现货交易确认</span>
                </div>
            ),
            icon: null,
            width: 480,
            centered: true,
            className: 'dark-theme-modal',
            content: (
                <div style={{fontSize: '14px', lineHeight: '1.6'}}>
                    {isLiveTrading && (
                        <p style={{color: 'var(--pt-bear)', fontWeight: 'bold', marginBottom: '16px'}}>
                            ⚠️ 您正在进行实盘交易，资金将真实投入市场
                        </p>
                    )}

                    <div style={{
                        background: 'rgba(7, 10, 18, 0.3)',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        border: '1px solid #404040'
                    }}>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>交易对：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>{selectedInstrument}</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>交易方向：</Text></Col>
                            <Col><Text style={{color: side === 'buy' ? 'var(--pt-bull)' : 'var(--pt-bear)'}}>
                                {side === 'buy' ? '买入' : '卖出'}
                            </Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>订单类型：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>{orderType === 'market' ? '市价单' : '限价单'}</Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>委托价格：</Text></Col>
                            <Col><Text style={{color: '#ffffff'}}>
                                {orderType === 'limit' && limitPrice
                                    ? `$${limitPrice.toFixed(4)}`
                                    : orderPrice
                                        ? `$${orderPrice.toFixed(4)} (市价)`
                                        : '获取中...'
                                }
                            </Text></Col>
                        </Row>
                        <Row justify="space-between" style={{marginBottom: '8px'}}>
                            <Col><Text style={{color: '#a0a0a0'}} strong>
                                {side === 'buy' ? `买入数量（${spotPercent}%）` : `卖出数量（${spotSellForcedFull ? 100 : spotPercent}%）`}：
                            </Text></Col>
                            <Col><Text
                                style={{color: '#ffffff'}}>{formatEffectiveDecimal(orderQty, 2, 0, 8)}</Text></Col>
                        </Row>
                        {orderUsd > 0 && (
                            <Row justify="space-between" style={{marginBottom: '8px'}}>
                                <Col><Text style={{color: '#a0a0a0'}} strong>预计金额：</Text></Col>
                                <Col><Text
                                    style={{color: '#ffffff'}}>${formatEffectiveDecimal(orderUsd, 2, 0, 8)}</Text></Col>
                            </Row>
                        )}
                        {side === 'buy' && tpPriceDisplay !== null && (
                            <Row justify="space-between" style={{marginBottom: '8px'}}>
                                <Col><Text style={{color: '#a0a0a0'}} strong>止盈触发价：</Text></Col>
                                <Col><Text style={{color: 'var(--pt-bull)'}}>
                                    ${tpPriceDisplay.toFixed(4)}
                                    {useTakeProfitPct && takeProfitPct ? ` (${takeProfitPct}%)` : ''}
                                </Text></Col>
                            </Row>
                        )}
                        {side === 'buy' && slPriceDisplay !== null && (
                            <Row justify="space-between" style={{marginBottom: '8px'}}>
                                <Col><Text style={{color: '#a0a0a0'}} strong>止损触发价：</Text></Col>
                                <Col><Text style={{color: 'var(--pt-bear)'}}>
                                    ${slPriceDisplay.toFixed(4)}
                                    {useStopLossPct && stopLossPct ? ` (${stopLossPct}%)` : ''}
                                </Text></Col>
                            </Row>
                        )}
                    </div>
                </div>
            ),
            okText: '确认下单',
            cancelText: '取消',
            onOk: async () => {
                await executeSpotOrder(side);
            }
        });
    };

    // 处理下单
    const handleSubmit = async (direction: 'long' | 'short') => {
        // 开多使用side="buy"，开空使用side="sell"
        const side = direction === 'long' ? 'buy' : 'sell';
        if ('SPOT' === tradeMode) {
            await handleSpotSubmit(side);
            return;
        }
        if (!selectedApiKey) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>请选择API Key</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        请先选择一个API Key再进行交易
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        if (!selectedInstrument) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>请选择合约品种</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        请先选择一个合约品种再进行交易
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        const selectedApiKeyData = apiKeys.find(key => key.keyId === selectedApiKey);
        if (!selectedApiKeyData) {
            Modal.error({
                title: <span style={{color: '#ffffff'}}>API Key不存在</span>,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        所选择的API Key不存在，请重新选择
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal'
            });
            return;
        }

        if (accountBalanceLoading || selectedMargin === null) {
            Modal.info({
                title: <span style={{color: '#ffffff'}}>可用保证金计算中</span>,
                icon: null,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        正在获取账户可用保证金，请稍后重试
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal',
                okText: '知道了'
            });
            return;
        }

        if (selectedMargin < MIN_ORDER_MARGIN_USDT) {
            showMinimumMarginWarning(selectedMargin);
            return;
        }

        if (!previewLoaded) {
            Modal.info({
                title: <span style={{color: '#ffffff'}}>订单规格计算中</span>,
                icon: null,
                content: (
                    <div style={{color: '#a0a0a0'}}>
                        正在按合约规格计算下单数量，请稍后重试
                    </div>
                ),
                centered: true,
                className: 'dark-theme-modal',
                okText: '知道了'
            });
            return;
        }

        if (previewActualMargin !== null && previewActualMargin < MIN_ORDER_MARGIN_USDT) {
            showMinimumMarginWarning(previewActualMargin);
            return;
        }

        // 显示交易确认对话框
        showTradingConfirmDialog(direction, side, selectedApiKeyData);
    };

    // 处理价格更新 - 使用节流优化
    const throttledPriceUpdate = useThrottle((instrumentId: string, price: number) => {
        if (instrumentId === selectedInstrument) {
            // console.log('收到价格更新:', instrumentId, price);
            setCurrentPrice(price);
            setCurrentMarkPrice(price); // 同时更新标记价格
        }
    }, 1000); // 1000ms节流间隔，优化性能

    // 保持原有的函数签名以便组件使用
    const handlePriceUpdate = throttledPriceUpdate;

    // 执行现货下单(市价/限价,止盈止损走后端独立策略委托)
    const executeSpotOrder = async (side: 'buy' | 'sell') => {
        setLoading(true);
        try {
            // 验证限价单必须设置价格
            if (orderType === 'limit' && (!limitPrice || limitPrice <= 0)) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>限价单价格无效</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            限价单必须设置有效价格
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                setLoading(false);
                return;
            }

            // 现货卖出无需止盈止损(卖出已有资产不会再挂卖出策略委托,后端同口径校验)
            const isSpotSell = 'sell' === side;
            // 现货买入止盈止损(策略委托):百分比模式按基准价换算触发价,持有现货=看涨,方向固定long
            let spotTakeProfitPrice: number | null = null;
            let spotStopLossPrice: number | null = null;
            if (!isSpotSell) {
                if (takeProfitEnabled) {
                    spotTakeProfitPrice = await calculateTakeProfitPrice(
                        takeProfitEnabled, useTakeProfitPct, takeProfitPct, takeProfitPrice, 'buy', 'long'
                    );
                }
                if (stopLossEnabled) {
                    spotStopLossPrice = await calculateStopLossPrice(
                        stopLossEnabled, useStopLossPct, stopLossPct, stopLossPrice, 'buy', 'long'
                    );
                }
            }
            const response = await tradingService.placeOrder({
                apiKeyId: selectedApiKey!,
                instId: selectedInstrument,
                instType: 'SPOT',
                side,
                orderType,
                sz: spotQtyRef.current ?? undefined,
                px: orderType === 'limit' && limitPrice !== null ? limitPrice : undefined,
                lever: 1, // 现货无杠杆,占位值(后端SPOT分支忽略)
                takeProfitPrice: !isSpotSell && spotTakeProfitPrice !== null ? spotTakeProfitPrice : undefined,
                stopLossPrice: !isSpotSell && spotStopLossPrice !== null ? spotStopLossPrice : undefined,
                source: 'web' as const // 用户手动下单
            });

            if (response.data.success) {
                const isManualRiskMode = response.data.data?.isManualRiskMode || false;

                Modal.success({
                    title: <span style={{color: '#ffffff'}}>
                        {isManualRiskMode ? '进入风控审核' : '现货下单成功'}
                    </span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            {isManualRiskMode ? (
                                <>
                                    <div>订单已进入风控审核流程，请等待风控审核</div>
                                    {response.data.data?.orderId && (
                                        <div style={{marginTop: 8, color: '#888'}}>
                                            订单ID: {response.data.data.orderId}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div>{response.data.message || '现货订单已提交'}</div>
                                    {response.data.data?.orderId && (
                                        <div style={{marginTop: 8, color: '#888'}}>
                                            订单ID: {response.data.data.orderId}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });

                onTradeSuccess({
                    apiKeyId: selectedApiKey!,
                    instId: selectedInstrument,
                    side,
                    posSide: '', // 现货无持仓方向
                    orderType,
                    px: orderType === 'limit' && limitPrice !== null ? limitPrice.toString() : undefined
                });
                // 成交后刷新现货持仓(卖出后可卖数量/买入后资产列表均需更新)
                if (selectedApiKey) {
                    loadSpotPositions(selectedApiKey).catch(() => undefined);
                }
            } else {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>现货下单失败</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            {response.data.message || '现货下单失败，请重试'}
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
            }
        } catch (error: any) {
            handleTradingError(error);
        } finally {
            setLoading(false);
        }
    };

    // 执行下单
    const executeOrder = async (side: 'buy' | 'sell', direction: 'long' | 'short') => {
        setLoading(true);
        try {
            // 验证限价单必须设置价格
            if (orderType === 'limit' && (!limitPrice || limitPrice <= 0)) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>限价单价格无效</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            限价单必须设置有效价格
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                setLoading(false);
                return;
            }

            // 验证止盈止损设置
            if (takeProfitEnabled && !useTakeProfitPct && (!takeProfitPrice || takeProfitPrice <= 0)) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>止盈价格无效</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            请设置有效的止盈价格
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                setLoading(false);
                return;
            }
            if (stopLossEnabled && !useStopLossPct && (!stopLossPrice || stopLossPrice <= 0)) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>止损价格无效</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            请设置有效的止损价格
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                setLoading(false);
                return;
            }
            if (takeProfitEnabled && useTakeProfitPct && (!takeProfitPct || takeProfitPct <= 0)) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>止盈百分比无效</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            请设置有效的止盈百分比
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                setLoading(false);
                return;
            }
            if (stopLossEnabled && useStopLossPct && (!stopLossPct || stopLossPct <= 0)) {
                Modal.error({
                    title: <span style={{color: '#ffffff'}}>止损百分比无效</span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            请设置有效的止损百分比
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                setLoading(false);
                return;
            }

            // 计算止盈价格（如果使用百分比）
            const calculatedTakeProfitPrice = await calculateTakeProfitPrice(
                takeProfitEnabled,
                useTakeProfitPct,
                takeProfitPct,
                takeProfitPrice,
                side,
                direction
            );

            // 计算止损价格（如果使用百分比）
            const calculatedStopLossPrice = await calculateStopLossPrice(
                stopLossEnabled,
                useStopLossPct,
                stopLossPct,
                stopLossPrice,
                side,
                direction
            );

            // 调试信息：显示最终传递给API的参数
            // console.log('=== 下单参数调试信息 ===');
            // console.log('合约:', selectedInstrument);
            // console.log('方向:', side, '(', direction, ')');
            // console.log('订单类型:', orderType);
            // console.log('数量:', selectedAmount);
            // console.log('杠杆:', 3);
            // console.log('限价:', orderType === 'limit' && limitPrice !== null ? limitPrice : '市价');
            // console.log('原始止盈设置:', {
            //     enabled: takeProfitEnabled,
            //     usePercentage: useTakeProfitPct,
            //     percentage: takeProfitPct,
            //     fixedPrice: takeProfitPrice
            // });
            // console.log('原始止损设置:', {
            //     enabled: stopLossEnabled,
            //     usePercentage: useStopLossPct,
            //     percentage: stopLossPct,
            //     fixedPrice: stopLossPrice
            // });

            // 强制调试：如果用户输入了百分比但useTakeProfitPct为false，强制修复
            let finalTakeProfitPrice = calculatedTakeProfitPrice;
            let finalStopLossPrice = calculatedStopLossPrice;

            if (takeProfitEnabled && takeProfitPct && takeProfitPct > 0 && !useTakeProfitPct) {
                // console.warn('检测到止盈百分比输入但模式未切换，强制修复');
                setUseTakeProfitPct(true);
                // 重新计算
                finalTakeProfitPrice = await calculateTakeProfitPrice(
                    takeProfitEnabled, true, takeProfitPct, takeProfitPrice, side, direction
                );
            }

            if (stopLossEnabled && stopLossPct && stopLossPct > 0 && !useStopLossPct) {
                // console.warn('检测到止损百分比输入但模式未切换，强制修复');
                setUseStopLossPct(true);
                // 重新计算
                finalStopLossPrice = await calculateStopLossPrice(
                    stopLossEnabled, true, stopLossPct, stopLossPrice, side, direction
                );
            }

            // console.log('计算后止盈价格:', finalTakeProfitPrice);
            // console.log('计算后止损价格:', finalStopLossPrice);
            // console.log('==========================');

            const response = await tradingService.placeOrder({
                apiKeyId: selectedApiKey!,
                instId: selectedInstrument,
                side,
                orderType,
                allocationPercent: selectedAllocationPercent,
                px: orderType === 'limit' && limitPrice !== null ? limitPrice : undefined,
                posSide: direction,
                lever: 3,
                takeProfitPrice: finalTakeProfitPrice !== null ? finalTakeProfitPrice : undefined,
                stopLossPrice: finalStopLossPrice !== null ? finalStopLossPrice : undefined,
                source: 'web' as const // 用户手动下单
            });

            if (response.data.success) {
                // 根据风控模式显示不同的成功提示
                const isManualRiskMode = response.data.data?.isManualRiskMode || false;

                Modal.success({
                    title: <span style={{color: '#ffffff'}}>
                        {isManualRiskMode ? '进入风控审核' : '下单成功'}
                    </span>,
                    content: (
                        <div style={{color: '#a0a0a0'}}>
                            {isManualRiskMode ? (
                                <>
                                    <div>订单已进入风控审核流程，请等待风控审核</div>
                                    {response.data.data?.orderId && (
                                        <div style={{marginTop: '8px'}}>订单ID: {response.data.data.orderId}</div>
                                    )}
                                </>
                            ) : (
                                <>
                                    订单ID: {response.data.data?.orderId}
                                </>
                            )}
                        </div>
                    ),
                    centered: true,
                    className: 'dark-theme-modal'
                });
                onTradeSuccess({
                    apiKeyId: selectedApiKey!,
                    instId: selectedInstrument,
                    side,
                    posSide: direction,
                    orderType,
                    px: orderType === 'limit' && limitPrice !== null ? String(limitPrice) : undefined,
                });
            } else {
                handleTradingError({response: {data: {message: response.data.message}}});
            }
        } catch (error: any) {
            handleTradingError(error);
        } finally {
            setLoading(false);
        }
    };

    // 计算止盈价格的辅助函数
    const calculateTakeProfitPrice = async (
        enabled: boolean,
        usePercentage: boolean,
        percentage: number | null,
        fixedPrice: number | null,
        side: 'buy' | 'sell',
        direction: 'long' | 'short'
    ): Promise<number | null> => {
        // console.log(`止盈价格计算开始: enabled=${enabled}, usePercentage=${usePercentage}, percentage=${percentage}, fixedPrice=${fixedPrice}, direction=${direction}`);

        if (!enabled) {
            // console.log('止盈未启用，返回null');
            return null;
        }

        if (!usePercentage) {
            // console.log('使用固定止盈价格:', fixedPrice);
            return fixedPrice;
        }

        if (!percentage || percentage <= 0) {
            // console.log('百分比无效，返回固定价格:', fixedPrice);
            return fixedPrice;
        }

        try {
            // 检查是否有实时价格
            if (currentPrice === null || currentPrice === 0) {
                throw new Error('未获取到实时价格，无法计算止盈价格。请等待价格数据加载完成后再试。');
            }

            // console.log('使用组件实时价格:', currentPrice, '合约:', selectedInstrument);

            // 计算基准价格（限价单使用限价，市价单使用当前价格）
            const basePrice = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
            // console.log(`基准价格: ${basePrice} (订单类型: ${orderType}, 限价: ${limitPrice})`);

            // 止盈：开多时价格上涨，开空时价格下跌
            let multiplier;
            // console.log(`DEBUG: 计算前 - direction=${direction}, percentage=${percentage}, percentage/100=${percentage / 100}`);
            if (direction === 'long') {
                // 开多：止盈价格 = 基准价格 * (1 + 百分比)
                multiplier = 1 + (percentage / 100);
                // console.log(`DEBUG: 开多止盈 - 基准价格=${basePrice}, multiplier=${multiplier}, 结果=${basePrice * multiplier}`);
            } else {
                // 开空：止盈价格 = 基准价格 * (1 - 百分比)
                multiplier = 1 - (percentage / 100);
                // console.log(`DEBUG: 开空止盈 - 基准价格=${basePrice}, multiplier=${multiplier}, 结果=${basePrice * multiplier}`);
            }

            const calculatedPrice = basePrice * multiplier;
            // console.log(`最终止盈价格计算: 方向=${direction}, 基准价格=${basePrice}, 百分比=${percentage}%, 计算价格=${calculatedPrice}`);

            return calculatedPrice;
        } catch (error) {
            // console.error('计算止盈价格失败:', error);
            return fixedPrice;
        }
    };

    // 计算止损价格的辅助函数
    const calculateStopLossPrice = async (
        enabled: boolean,
        usePercentage: boolean,
        percentage: number | null,
        fixedPrice: number | null,
        side: 'buy' | 'sell',
        direction: 'long' | 'short'
    ): Promise<number | null> => {
        // console.log(`止损价格计算开始: enabled=${enabled}, usePercentage=${usePercentage}, percentage=${percentage}, fixedPrice=${fixedPrice}, direction=${direction}`);

        if (!enabled) {
            // console.log('止损未启用，返回null');
            return null;
        }

        if (!usePercentage) {
            // console.log('使用固定止损价格:', fixedPrice);
            return fixedPrice;
        }

        if (!percentage || percentage <= 0) {
            // console.log('百分比无效，返回固定价格:', fixedPrice);
            return fixedPrice;
        }

        try {
            // 检查是否有实时价格
            if (currentPrice === null || currentPrice === 0) {
                throw new Error('未获取到实时价格，无法计算止损价格。请等待价格数据加载完成后再试。');
            }

            // console.log('使用组件实时价格:', currentPrice, '合约:', selectedInstrument);

            // 计算基准价格（限价单使用限价，市价单使用当前价格）
            const basePrice = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
            // console.log(`基准价格: ${basePrice} (订单类型: ${orderType}, 限价: ${limitPrice})`);

            // 止损：开多时价格下跌，开空时价格上涨
            let multiplier;
            if (direction === 'long') {
                // 开多：止损价格 = 基准价格 * (1 - 百分比)
                multiplier = 1 - (percentage / 100);
                // console.log(`开多止损: 基准价格=${basePrice} * (1 - ${percentage / 100}) = ${basePrice * multiplier}`);
            } else {
                // 开空：止损价格 = 基准价格 * (1 + 百分比)
                multiplier = 1 + (percentage / 100);
                // console.log(`开空止损: 基准价格=${basePrice} * (1 + ${percentage / 100}) = ${basePrice * multiplier}`);
            }

            const calculatedPrice = basePrice * multiplier;
            // console.log(`最终止损价格计算: 方向=${direction}, 基准价格=${basePrice}, 百分比=${percentage}%, 计算价格=${calculatedPrice}`);

            return calculatedPrice;
        } catch (error) {
            // console.error('计算止损价格失败:', error);
            return fixedPrice;
        }
    };

    // 获取预估数量和名义价值
    const {quantity, notional} = {
        quantity: estimatedQuantity,
        notional: estimatedNotional
    };

    // 当切换到限价单时自动填入当前价格（仅当限价为空时）
    useEffect(() => {
        if (orderType === 'limit' && (!limitPrice || limitPrice === 0) && currentPrice && selectedInstrument) {
            setLimitPrice(currentPrice);
            // 移除notification，只保留自动填入功能
        }
    }, [orderType]);

    // 限价单模式下，价格变化时自动更新委托价格（仅在用户未手动编辑时）
    useEffect(() => {
        if (orderType === 'limit' && currentPrice && selectedInstrument &&
            !hasManuallyEditedPrice && !isUserEditing) {
            const previousPrice = limitPrice;
            setLimitPrice(currentPrice);

            // 添加价格更新视觉反馈（闪烁效果）
            if (previousPrice && previousPrice !== currentPrice) {
                setPriceFlashClass(currentPrice > previousPrice ? 'price-flash-up' : 'price-flash-down');
                setTimeout(() => setPriceFlashClass(''), 300);
            }
        }
    }, [currentPrice, selectedInstrument, orderType, hasManuallyEditedPrice, isUserEditing]);

    const selectedApiKeyData = apiKeys.find(key => key.keyId === selectedApiKey);
    const previewQuantity = useMemo(() => {
        const orderPrice = orderType === 'limit' && limitPrice ? limitPrice : currentPrice;
        if (!orderPrice || !previewHasMargin) {
            return 0;
        }
        return estimateContractQuantity({
            amount: previewActualMargin!,
            leverage: 3,
            price: orderPrice,
            ctVal: contractSpec.ctVal,
            lotSz: contractSpec.lotSz,
            minSz: contractSpec.minSz
        });
    }, [orderType, limitPrice, currentPrice, selectedAllocationPercent, contractSpec, previewActualMargin, previewHasMargin]);

    return (
        <Card className="trading-form-card"
              title={<><DollarOutlined/> {'SPOT' === tradeMode ? '现货交易' : '合约交易'}</>}>
            <div className="trading-form">
                <Form form={form} layout="vertical">

                    {/* 实盘风险提示 */}
                    {selectedApiKeyData?.isLiveTrading && (
                        <Alert
                            message="实盘交易风险提示"
                            description="当前选择的是实盘交易模式，所有交易将使用真实资金，请谨慎操作。"
                            type="warning"
                            showIcon
                            style={{marginBottom: 16}}
                        />
                    )}

                    {/* 合约品种选择(现货模式显示交易对) */}
                    <Form.Item label={<><Text strong>{'SPOT' === tradeMode ? '交易对' : '合约品种'}</Text></>}>
                        <div className="instrument-selector-container">
                            {selectedApiKey ? (
                                <Top30InstrumentSelector
                                    selectedInstrument={selectedInstrument}
                                    onInstrumentChange={setSelectedInstrument}
                                    onPriceUpdate={handlePriceUpdate}
                                    apiKeyId={selectedApiKey}
                                    instType={tradeMode}
                                    count={'SPOT' === tradeMode ? 10 : 15}
                                />
                            ) : (
                                <div style={{color: 'rgba(255, 255, 255, 0.45)'}}>
                                    请先选择 API Key
                                </div>
                            )}
                        </div>
                    </Form.Item>

                    {/* 合约详细信息面板 */}
                    <InstrumentDetailPanel
                        instId={selectedInstrument}
                        onFourHourAvgChange={setFourHourAvgChange}
                        apiKeyId={selectedApiKey}
                        markPrice={currentMarkPrice}
                    />

                    {/* 订单类型 */}
                    <Form.Item label={<><Text strong>订单类型</Text></>}>
                        <Select
                            value={orderType}
                            onChange={setOrderType}
                            size="large"
                            style={{width: '100%'}}
                        >
                            <Select.Option value="market">市价单</Select.Option>
                            <Select.Option value="limit">限价单</Select.Option>
                        </Select>
                    </Form.Item>

                    {/* 限价单价格输入框 */}
                    {orderType === 'limit' && (
                        <Form.Item
                            label={
                                <>
                                    <><Text strong>委托价格</Text></>
                                    <Tooltip title="限价单必须设置的成交价格">
                                        <InfoCircleOutlined style={{marginLeft: 8, color: '#888'}}/>
                                    </Tooltip>
                                </>
                            }
                        >
                            <div style={{position: 'relative'}}>
                                <InputNumber
                                    value={limitPrice}
                                    onChange={(value) => {
                                        setLimitPrice(value);
                                        setHasManuallyEditedPrice(true);  // 标记用户已手动编辑
                                    }}
                                    onFocus={() => setIsUserEditing(true)}
                                    onBlur={() => setIsUserEditing(false)}
                                    placeholder="输入限价单价格"
                                    size="large"
                                    style={{width: '100%'}}
                                    precision={4}
                                    min={0}
                                    step={0.0001}
                                    className={priceFlashClass}
                                    formatter={(value) => value ? formatEffectiveDecimal(value, 2, 0, 4) : ''}
                                    parser={(value) => parseFloat(value!.replace(/[^\d.-]/g, '') || '0')}
                                />
                                {hasManuallyEditedPrice && currentPrice && (
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => {
                                            setLimitPrice(currentPrice);
                                            setHasManuallyEditedPrice(false);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            padding: '0 4px',
                                            height: 'auto',
                                            fontSize: '12px'
                                        }}
                                    >
                                        恢复市价
                                    </Button>
                                )}
                            </div>
                        </Form.Item>
                    )}

                    {/* 现货:买入/卖出方向 + 百分比(买入按可用USDT,卖出按可卖持仓) */}
                    {'SPOT' === tradeMode ? (
                        <>
                            {/* 方向切换:决定比例基数 */}
                            <Form.Item label={<><Text strong>交易方向</Text></>} style={{marginBottom: 12}}>
                                <Segmented
                                    block
                                    size="large"
                                    value={spotSide}
                                    onChange={(value) => setSpotSide(value as 'buy' | 'sell')}
                                    options={[
                                        {
                                            label: <span style={{color: 'var(--pt-bull)', fontWeight: 600}}>买入</span>,
                                            value: 'buy'
                                        },
                                        {
                                            label: <span style={{color: 'var(--pt-bear)', fontWeight: 600}}>卖出</span>,
                                            value: 'sell'
                                        }
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<><Text
                                    strong>{'buy' === spotSide ? '买入比例（按可用USDT）' : '卖出比例（按现货持仓）'}</Text></>}>
                                <div className="amount-selector amount-selector--percent" style={{width: '100%'}}>
                                    <div className="amount-selector__body" style={{width: '100%'}}>
                                        <div className="amount-selector__controls">
                                            <div className="amount-selector__input-row">
                                                <span>{'buy' === spotSide ? '买入比例' : '卖出比例'}</span>
                                                <InputNumber<number>
                                                    className="amount-selector__input"
                                                    value={spotSellForcedFull ? 100 : spotPercent}
                                                    onChange={(value) => {
                                                        if (spotSellForcedFull) return; // 持仓低于最小交易单位:比例不可调整
                                                        if (value !== null) {
                                                            setSpotPercent(Math.min(Math.max(Math.round(value), 1), 100));
                                                        }
                                                    }}
                                                    min={1}
                                                    max={100}
                                                    step={1}
                                                    precision={0}
                                                    size="small"
                                                    controls={true}
                                                    disabled={spotSellForcedFull}
                                                    formatter={(value) => `${value}%`}
                                                    parser={(value) => parseFloat(value?.replace('%', '') || '0')}
                                                />
                                            </div>
                                            <div className="amount-selector__presets" aria-label="常用买卖比例">
                                                {SPOT_QUICK_PERCENTS.map((pct) => {
                                                    const active = (spotSellForcedFull ? 100 : spotPercent) === pct;
                                                    return (
                                                        <Button
                                                            key={pct}
                                                            size="small"
                                                            className="amount-selector__preset"
                                                            type={active ? 'primary' : 'default'}
                                                            disabled={spotSellForcedFull}
                                                            onClick={() => !spotSellForcedFull && setSpotPercent(pct)}
                                                        >
                                                            {pct}%
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                        <div className="amount-selector__valuation" aria-live="polite">
                                            {'buy' === spotSide ? (
                                                <>
                                                    <div className="amount-selector__metric">
                                                        <span>可用USDT</span>
                                                        <strong>
                                                            {accountAvailableMargin !== null
                                                                ? accountAvailableMargin.toLocaleString('en-US', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })
                                                                : (accountBalanceLoading ? '获取中' : '--')}
                                                        </strong>
                                                    </div>
                                                    <div
                                                        className={'amount-selector__metric amount-selector__metric--primary'
                                                            + (spotEstUsdt !== null && spotEstUsdt > 0 && spotEstUsdt < MIN_ORDER_MARGIN_USDT
                                                                ? ' amount-selector__metric--below-min' : '')}>
                                                        <span>买入金额</span>
                                                        <strong>
                                                            {spotEstUsdt !== null
                                                                ? `${spotEstUsdt.toLocaleString('en-US', {
                                                                    minimumFractionDigits: 2,
                                                                    maximumFractionDigits: 2
                                                                })} USDT`
                                                                : '--'}
                                                        </strong>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="amount-selector__metric">
                                                        <span>可卖数量</span>
                                                        <strong>
                                                            {spotHolding
                                                                ? formatEffectiveDecimal(spotHolding.available, 2, 0, 8)
                                                                : (spotPositionsLoading ? '获取中' : '--')}
                                                        </strong>
                                                    </div>
                                                    <div
                                                        className="amount-selector__metric amount-selector__metric--primary">
                                                        <span>卖出数量</span>
                                                        <strong>
                                                            {spotEstQty !== null
                                                                ? formatEffectiveDecimal(spotEstQty, 2, 0, 8)
                                                                : '--'}
                                                        </strong>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* 约束与换算提示 */}
                                {'sell' === spotSide && !spotHolding && !spotPositionsLoading && (
                                    <div style={{marginTop: 6, fontSize: '12px', color: 'var(--pt-amber)'}}>
                                        当前交易对暂无现货持仓
                                    </div>
                                )}
                                {'sell' === spotSide && spotSellForcedFull && (
                                    <div style={{marginTop: 6, fontSize: '12px', color: 'var(--pt-amber)'}}>
                                        持仓 {formatEffectiveDecimal(spotHolding?.available ?? 0, 2, 0, 8)} 低于最小交易单位{' '}
                                        {formatEffectiveDecimal(spotMinQty!, 2, 0, 8)}，仅支持按 100% 全额卖出
                                    </div>
                                )}
                                {'buy' === spotSide && spotEstUsdt !== null && spotEstUsdt > 0
                                    && spotEstUsdt < MIN_ORDER_MARGIN_USDT && (
                                        <div style={{marginTop: 6, fontSize: '12px', color: 'var(--pt-amber)'}}>
                                            本次买入金额低于最低 {MIN_ORDER_MARGIN_USDT} USDT，可能被交易所拒绝
                                        </div>
                                    )}
                                {spotEstQty !== null && spotEstQty > 0 && spotPriceForCalc !== null && (
                                    <div style={{
                                        marginTop: 6,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Text style={{fontSize: '12px', color: '#999'}}>
                                            {'buy' === spotSide ? '预估买入数量' : '预计回款(USDT)'}
                                        </Text>
                                        <Text style={{fontSize: '12px', color: '#ffffff'}}>
                                            {'buy' === spotSide
                                                ? `${formatEffectiveDecimal(spotEstQty, 2, 0, 8)} ${selectedInstrument.replace(/-USDT.*$/, '')}`
                                                : `≈ $${formatEffectiveDecimal(spotEstUsdt ?? 0, 2, 0, 8)}`}
                                        </Text>
                                    </div>
                                )}
                            </Form.Item>
                        </>
                    ) : (
                        <Form.Item label={<><Text strong>开仓比例（可用保证金）</Text></>}>
                            <AmountSelector
                                selectedPercent={selectedAllocationPercent}
                                onPercentChange={setSelectedAllocationPercent}
                                availableMarginUsdt={accountAvailableMargin}
                                isLoading={accountBalanceLoading}
                                minOrderMarginUsdt={MIN_ORDER_MARGIN_USDT}
                            />
                        </Form.Item>
                    )}

                    {/* 杠杆倍数(仅合约模式) */}
                    {'SWAP' === tradeMode && (
                        <Form.Item label={<><Text strong>杠杆倍数</Text></>}>
                            <div className="lever-display">
                                <Text strong style={{fontSize: '16px'}}>3x</Text>
                                <Text type="secondary" style={{marginLeft: 8}}>固定杠杆</Text>
                            </div>
                        </Form.Item>
                    )}

                    {/* 止盈止损设置(现货仅买入方向展示:卖出后无持仓,后端亦拒绝挂策略委托) */}
                    {('SWAP' === tradeMode || ('SPOT' === tradeMode && 'buy' === spotSide)) && (
                        <Form.Item label={<><Text strong>止盈止损设置</Text></>}>
                            <div className="profit-loss-settings">
                                {/* 止盈/止损:百分比与固定价格双模式(永续多空均可用;现货仅买入方向,卖出后无持仓不再挂策略委托) */}
                                <>
                                    <div className="profit-loss-item">
                                        <div className="profit-loss-header">
                                            <Checkbox
                                                checked={takeProfitEnabled}
                                                onChange={(e) => setTakeProfitEnabled(e.target.checked)}
                                            >
                                                <Text>止盈</Text>
                                            </Checkbox>
                                            <Tooltip title="当价格上涨到指定价格或百分比时自动卖出获利">
                                                <InfoCircleOutlined style={{marginLeft: 4, color: '#888'}}/>
                                            </Tooltip>
                                        </div>

                                        {takeProfitEnabled && (
                                            <div className="profit-loss-controls" style={{marginTop: 12}}>
                                                <Row gutter={8}>
                                                    <Col span={12}>
                                                        <Checkbox
                                                            checked={useTakeProfitPct}
                                                            onChange={(e) => {
                                                                setUseTakeProfitPct(e.target.checked);
                                                                // 选择百分比时，清理固定价格
                                                                if (e.target.checked) {
                                                                    setTakeProfitPrice(null);
                                                                }
                                                            }}
                                                        >
                                                            百分比
                                                        </Checkbox>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Checkbox
                                                            checked={!useTakeProfitPct}
                                                            onChange={(e) => {
                                                                setUseTakeProfitPct(!e.target.checked);
                                                                // 选择固定价格时，清理百分比
                                                                if (!e.target.checked) {
                                                                    setTakeProfitPct(null);
                                                                }
                                                            }}
                                                        >
                                                            固定价格
                                                        </Checkbox>
                                                    </Col>
                                                </Row>

                                                <div style={{marginTop: 8}}>
                                                    {useTakeProfitPct ? (
                                                        <>
                                                            <InputNumber
                                                                value={takeProfitPct}
                                                                onChange={(value) => {
                                                                    setTakeProfitPct(value);
                                                                    // 如果用户输入了百分比，自动切换到百分比模式并清理固定价格
                                                                    if (value && value > 0) {
                                                                        setUseTakeProfitPct(true);
                                                                        setTakeProfitPrice(null);
                                                                    }
                                                                }}
                                                                placeholder={isCalculatingProfitLoss ? "计算中..." : "止盈百分比"}
                                                                size="middle"
                                                                style={{width: '100%'}}
                                                                precision={2}
                                                                min={0.01}
                                                                step={0.01}
                                                                disabled={isCalculatingProfitLoss}
                                                                formatter={value => `${value}%`}
                                                                parser={value => parseFloat(value!.replace('%', '') || '0')}
                                                            />
                                                            {fourHourAvgChange !== null && (
                                                                <div
                                                                    style={{
                                                                        marginTop: 4,
                                                                        fontSize: '12px',
                                                                        color: '#666'
                                                                    }}>
                                                                    💡
                                                                    建议值: {parseFloat((Math.abs(fourHourAvgChange) * 1.2).toFixed(2))}%
                                                                    (基于4H平均: {parseFloat(Math.abs(fourHourAvgChange).toFixed(2))}%)
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <InputNumber
                                                            value={takeProfitPrice}
                                                            onChange={(value) => {
                                                                setTakeProfitPrice(value);
                                                                // 如果用户输入了固定价格，自动切换到固定价格模式并清理百分比
                                                                if (value && value > 0) {
                                                                    setUseTakeProfitPct(false);
                                                                    setTakeProfitPct(null);
                                                                }
                                                            }}
                                                            placeholder="止盈价格"
                                                            size="middle"
                                                            style={{width: '100%'}}
                                                            precision={4}
                                                            min={0}
                                                            step={0.0001}
                                                            formatter={(value) => value ? formatEffectiveDecimal(value, 2, 0, 4) : ''}
                                                            parser={(value) => parseFloat(value!.replace(/[^\d.-]/g, '') || '0')}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* 分割线 */}
                                    <div style={{height: 1, backgroundColor: '#f0f0f0', margin: '16px 0'}}/>

                                    {/* 止损设置 */}
                                    <div className="profit-loss-item">
                                        <div className="profit-loss-header">
                                            <Checkbox
                                                checked={stopLossEnabled}
                                                onChange={(e) => setStopLossEnabled(e.target.checked)}
                                            >
                                                <Text>止损</Text>
                                            </Checkbox>
                                            <Tooltip title="当价格下跌到指定价格或百分比时自动卖出限制亏损">
                                                <InfoCircleOutlined style={{marginLeft: 4, color: '#888'}}/>
                                            </Tooltip>
                                        </div>

                                        {stopLossEnabled && (
                                            <div className="profit-loss-controls" style={{marginTop: 12}}>
                                                <Row gutter={8}>
                                                    <Col span={12}>
                                                        <Checkbox
                                                            checked={useStopLossPct}
                                                            onChange={(e) => {
                                                                setUseStopLossPct(e.target.checked);
                                                                // 选择百分比时，清理固定价格
                                                                if (e.target.checked) {
                                                                    setStopLossPrice(null);
                                                                }
                                                            }}
                                                        >
                                                            百分比
                                                        </Checkbox>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Checkbox
                                                            checked={!useStopLossPct}
                                                            onChange={(e) => {
                                                                setUseStopLossPct(!e.target.checked);
                                                                // 选择固定价格时，清理百分比
                                                                if (!e.target.checked) {
                                                                    setStopLossPct(null);
                                                                }
                                                            }}
                                                        >
                                                            固定价格
                                                        </Checkbox>
                                                    </Col>
                                                </Row>

                                                <div style={{marginTop: 8}}>
                                                    {useStopLossPct ? (
                                                        <>
                                                            <InputNumber
                                                                value={stopLossPct}
                                                                onChange={(value) => {
                                                                    setStopLossPct(value);
                                                                    // 如果用户输入了百分比，自动切换到百分比模式并清理固定价格
                                                                    if (value && value > 0) {
                                                                        setUseStopLossPct(true);
                                                                        setStopLossPrice(null);
                                                                    }
                                                                }}
                                                                placeholder={isCalculatingProfitLoss ? "计算中..." : "止损百分比"}
                                                                size="middle"
                                                                style={{width: '100%'}}
                                                                precision={2}
                                                                min={0.01}
                                                                step={0.01}
                                                                disabled={isCalculatingProfitLoss}
                                                                formatter={value => `${value}%`}
                                                                parser={value => parseFloat(value!.replace('%', '') || '0')}
                                                            />
                                                            {fourHourAvgChange !== null && (
                                                                <div
                                                                    style={{
                                                                        marginTop: 4,
                                                                        fontSize: '12px',
                                                                        color: '#666'
                                                                    }}>
                                                                    💡
                                                                    建议值: {parseFloat((Math.abs(fourHourAvgChange) * 1.5).toFixed(2))}%
                                                                    (基于4H平均: {parseFloat(Math.abs(fourHourAvgChange).toFixed(2))}%)
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <InputNumber
                                                            value={stopLossPrice}
                                                            onChange={(value) => {
                                                                setStopLossPrice(value);
                                                                // 如果用户输入了固定价格，自动切换到固定价格模式并清理百分比
                                                                if (value && value > 0) {
                                                                    setUseStopLossPct(false);
                                                                    setStopLossPct(null);
                                                                }
                                                            }}
                                                            placeholder="止损价格"
                                                            size="middle"
                                                            style={{width: '100%'}}
                                                            precision={4}
                                                            min={0}
                                                            step={0.0001}
                                                            formatter={(value) => value ? formatEffectiveDecimal(value, 2, 0, 4) : ''}
                                                            parser={(value) => parseFloat(value!.replace(/[^\d.-]/g, '') || '0')}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            </div>
                        </Form.Item>
                    )}

                    {/* 预估信息(仅合约模式,现货直接按数量×价格展示) */}
                    {'SWAP' === tradeMode && selectedInstrument && (
                        <Form.Item label={<><Text strong>预估信息</Text></>}>
                            <div className="estimated-info">
                                <Row justify="space-between">
                                    <Col>
                                        <Text type="secondary">预估数量：</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>
                                            {quantity > 0
                                                ? formatEffectiveDecimal(previewQuantity, 2, 0, 8)
                                                : '0'
                                            }
                                        </Text>
                                    </Col>
                                </Row>
                                <Row justify="space-between" style={{marginTop: 4}}>
                                    <Col>
                                        <Text type="secondary">预估名义价值：</Text>
                                    </Col>
                                    <Col>
                                        <Text strong>
                                            ${notional > 0
                                            ? notional.toLocaleString('en-US', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })
                                            : '0'
                                        }
                                        </Text>
                                    </Col>
                                </Row>
                                <Row justify="space-between" style={{marginTop: 4}}>
                                    <Col>
                                        <Text type="secondary" style={{fontSize: '12px'}}>
                                            比例: {selectedAllocationPercent}%
                                        </Text>
                                    </Col>
                                    <Col>
                                        <Text type="secondary" style={{fontSize: '12px'}}>
                                            杠杆: 3x
                                        </Text>
                                    </Col>
                                </Row>
                                {(contractSpec.ctVal || contractSpec.lotSz || contractSpec.minSz) && (
                                    <Row justify="space-between" style={{marginTop: 4}}>
                                        <Col>
                                            <Text type="secondary" style={{fontSize: '12px'}}>
                                                规格: ctVal={formatEffectiveDecimal(contractSpec.ctVal || 1, 2, 0, 8)}
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary" style={{fontSize: '12px'}}>
                                                步长={formatEffectiveDecimal(contractSpec.lotSz || 1, 2, 0, 8)} /
                                                最小={formatEffectiveDecimal(contractSpec.minSz || 0, 2, 0, 8)}
                                            </Text>
                                        </Col>
                                    </Row>
                                )}
                                {currentPrice && (
                                    <Row justify="space-between" style={{marginTop: 4}}>
                                        <Col>
                                            <Text type="secondary" style={{fontSize: '12px'}}>
                                                {orderType === 'limit' && limitPrice
                                                    ? `委托价格: $${limitPrice.toLocaleString('en-US', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 4
                                                    })}`
                                                    : `当前价格: $${currentPrice.toLocaleString('en-US', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 4
                                                    })}`
                                                }
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Text type="secondary" style={{fontSize: '12px'}}>
                                                {orderType === 'limit' ? '限价单' : '市价单'}
                                            </Text>
                                        </Col>
                                    </Row>
                                )}
                            </div>
                        </Form.Item>
                    )}

                    {/* 交易按钮 */}
                    <Form.Item style={{marginBottom: 0}}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    className="trading-button buy-button"
                                    loading={loading}
                                    onClick={() => handleSubmit('long')}
                                    disabled={!selectedApiKey || !selectedInstrument}
                                >
                                    {'SPOT' === tradeMode ? '买入' : '开多'}
                                </Button>
                            </Col>
                            <Col span={12}>
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    className="trading-button sell-button"
                                    loading={loading}
                                    onClick={() => handleSubmit('short')}
                                    disabled={!selectedApiKey || !selectedInstrument}
                                >
                                    {'SPOT' === tradeMode ? '卖出' : '开空'}
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </div>
        </Card>
    );
};

export default React.memo(TradingForm);
